// requisitionsApi.ts: the ONE place every requisitions API-shape assumption lives.
//
// Read off .plans/purchase-requisitions/01-step1-contract.md and
// 02-step2-contract.md; the backend was built in parallel against the same
// contracts. If a shape moves, this file is
// the only one that should need to change.
//
// Money is integer cents on the wire. `estimated_unit_cost_cents` and
// `estimated_total_cents` are BIGINT columns and pg hands bigint back as a
// string unless a type parser says otherwise, so both are typed to accept a
// string. `quantity` is NUMERIC, same story. Every read goes through `toNumber`.

import { useApi } from '../apiHelpers';
import type { EventOption, PurchaseClassOption, ReceiptStatus } from '../Receipts/types';

export type { EventOption, PurchaseClassOption, ReceiptStatus };

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type RequisitionStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'bought'
  | 'arrived'
  | 'closed'
  | 'cancelled';

export type Urgency = 'low' | 'normal' | 'urgent';

export type ListScope = 'mine' | 'pending' | 'to_buy' | 'receipt_missing' | 'on_the_way' | 'all';

export type EventChannel = 'app' | 'slack' | 'system';

export type RequisitionAction =
  | 'created'
  | 'submitted'
  | 'withdrawn'
  | 'approved'
  | 'denied'
  | 'cancelled'
  | 'bought'
  | 'receipt_linked'
  | 'receipt_unlinked'
  | 'closed'
  | 'arrived'
  | 'shipment_added'
  | 'shipment_arrived'
  | 'shipment_removed'
  | 'line_inventory_changed'
  | 'line_received';

type Numeric = number | string;

export type Carrier = 'ups' | 'usps' | 'fedex' | 'amazon' | 'other';

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------

export interface PersonRef {
  id: string;
  name: string;
}

export interface RequisitionSummary {
  id: string;
  req_number: string;
  purpose: string;
  status: RequisitionStatus;
  urgency: Urgency;
  /** DATE, YYYY-MM-DD. Format with formatEventDate. */
  needed_by: string | null;
  requester: PersonRef | null;
  line_count: number;
  /** Sum of qty * cost over lines that carry a cost; null when none do. */
  estimated_total_cents: Numeric | null;
  receipt_to_follow: boolean;
  /** timestamptz. Format with formatTimestamp. */
  submitted_at: string | null;
  approved_at: string | null;
  bought_at: string | null;
  arrived_at: string | null;
  created_at: string;
  updated_at: string;
  // Beyond the step 2 contract's Summary: the receiving Requests tab's store
  // and "1 of 2" columns. Optional, so a row without them shows a dash.
  stores?: string[];
  shipments_total?: number;
  shipments_arrived?: number;
  /** Tracked lines with a catalog item and no received item yet: what is still to receive into stock. */
  lines_to_stock?: number;
  /** Tracked lines still waiting for a catalog item; the request cannot arrive until 0. */
  lines_need_item?: number;
}

export interface RequisitionLine {
  id: string;
  line_number: number;
  description: string;
  quantity: Numeric;
  link_url: string | null;
  store: string | null;
  estimated_unit_cost_cents: Numeric | null;
  notes: string | null;
  add_to_inventory: boolean;
  part_catalog_id: number | null;
  /** add_to_inventory with no part_catalog_id. The request is held at bought while any line is. */
  needs_catalog_item: boolean;
  /** The receipt item this line went into stock as; set once, then the line is locked. */
  receipt_item_id: string | null;
  /**
   * An item received for this request that no line has claimed: the receive
   * landed but recording it did not. Record it, never receive again. `id` is
   * the receipt item; `barcode` is its box's sticker.
   */
  unrecorded_box: { id: string; barcode: string } | null;
  part: LinePart | null;
}

export interface LinePart {
  id: number;
  sku: string;
  name: string | null;
}

export interface RequisitionEvent {
  action: RequisitionAction;
  channel: EventChannel;
  actor: PersonRef | null;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** What the server says the CALLER may do. The client never decides this. */
export interface RequisitionCan {
  edit: boolean;
  submit: boolean;
  cancel: boolean;
  approve: boolean;
  deny: boolean;
  mark_bought: boolean;
  close: boolean;
  mark_arrived: boolean;
  /** Orderer, on an approved, bought or arrived request. Also governs unlink. */
  link_receipt: boolean;
  /** Orderer, on a bought request. Also governs removing a package. */
  add_shipment: boolean;
  /**
   * May change Add to inventory on this request now. Once bought that only
   * means taking a tracked line off: `tick_inventory` is false there.
   */
  set_inventory: boolean;
  /** May turn Add to inventory on. Never true once bought. */
  tick_inventory: boolean;
  /** May pick the catalog item for a tracked line (procurement or admin). */
  set_catalog: boolean;
  /** Receiving or orderer, bought or arrived, with a tracked line ready to receive. */
  receive_lines: boolean;
}

export interface RequisitionDetail extends RequisitionSummary {
  lines: RequisitionLine[];
  purchase_class: { id: number; name: string } | null;
  event: { id: string; name: string } | null;
  approved_by: PersonRef | null;
  // The contract lists these two without a shape; read as the same
  // {id, name} as approved_by.
  bought_by: PersonRef | null;
  arrived_by: PersonRef | null;
  close_reason: string | null;
  events: RequisitionEvent[];
  can: RequisitionCan;
  /** Every package with a tracking number, oldest first. Visible to all. */
  shipments: Shipment[];
  /**
   * ABSENT, not empty, for a caller who may not see receipts (the requester).
   * Test the key, never the length.
   */
  receipts?: LinkedReceipt[];
}

export interface Shipment {
  id: string;
  carrier: Carrier;
  tracking_number: string;
  source: string;
  /** timestamptz; null until it arrives. */
  arrived_at: string | null;
  arrived_by: PersonRef | null;
}

export interface LinkedReceipt {
  pid: string;
  vendor: string | null;
  total_cents: Numeric | null;
  /** DATE, YYYY-MM-DD. */
  purchase_date: string | null;
  status: ReceiptStatus;
  linked_by: PersonRef | null;
  linked_at: string;
}

/** One of the caller's own recent receipts, offered for linking. */
export interface ReceiptCandidate {
  pid: string;
  vendor: string | null;
  total_cents: Numeric | null;
  purchase_date: string | null;
  status: ReceiptStatus;
}

/** A request this receipt could be linked to; `linked` when it already is. */
export interface LinkableRequisition extends RequisitionSummary {
  linked?: boolean;
}

export interface ReceiptRequisitionRef {
  id: string;
  req_number: string;
  purpose: string;
  approved_by: PersonRef | null;
  approved_at: string | null;
}

export interface ScanMatch {
  requisition: RequisitionSummary;
  shipment: Shipment;
}

/** The three answers a label scan can get. */
export type ScanResult =
  | { kind: 'match'; match: ScanMatch }
  | { kind: 'matches'; matches: ScanMatch[] }
  | { kind: 'miss'; missId: string };

/** One line listed in an approval: it becomes tracked, with its item when known. */
export interface InventoryLineInput {
  line_id: string;
  part_catalog_id?: number;
}

export interface LineInventoryInput {
  add_to_inventory: boolean;
  part_catalog_id?: number | null;
}

export type CostSource = 'receipt' | 'estimate';

/** What a tracked line probably cost, read off a linked receipt or the request. */
export interface CostHint {
  unit_cost_cents: number | null;
  /** Null when no receipt line matched and the request gave no estimate. */
  cost_source: CostSource | null;
  receipt_line?: { description: string; unit_price_cents: number } | null;
}

export interface MeResponse {
  is_approver: boolean;
  is_orderer: boolean;
  is_receiving: boolean;
  counts: {
    pending: number;
    to_buy: number;
    receipt_missing: number;
    on_the_way: number;
  };
}

// ---------------------------------------------------------------------------
// Write shapes
// ---------------------------------------------------------------------------

export interface DraftLineInput {
  description: string;
  quantity: number;
  link_url?: string | null;
  store?: string | null;
  estimated_unit_cost_cents?: number | null;
  notes?: string | null;
}

export interface DraftInput {
  purpose: string;
  needed_by: string | null;
  urgency: Urgency;
  purchase_class_id: number | null;
  event_id: string | null;
  lines: DraftLineInput[];
}

export interface RequisitionSettings {
  approver_user_ids: string[];
  orderer_user_ids: string[];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * A fresh object every render (useApi is), so never put the hook's return in a
 * dependency array. Call it at the top of the component and read it inside
 * effects, the way the PO pages use usePoApi.
 */
export function useRequisitionsApi() {
  const api = useApi();

  return {
    me: (): Promise<MeResponse> => api.get('/requisitions/me', { silent: true }),

    list: async (scope: ListScope, q?: string): Promise<RequisitionSummary[]> => {
      const qs = new URLSearchParams({ scope });
      if (q && q.trim()) qs.set('q', q.trim());
      const data = (await api.get(`/requisitions?${qs.toString()}`, { silent: true })) as {
        requisitions: RequisitionSummary[];
      };
      return data?.requisitions || [];
    },

    get: async (id: string): Promise<RequisitionDetail> =>
      unwrap(await api.get(`/requisitions/${id}`, { silent: true })),

    create: async (draft: DraftInput): Promise<RequisitionDetail> =>
      unwrap(await api.post('/requisitions', { body: draft, silent: true })),

    /** `lines`, when present, REPLACES every line. A pending request goes back to draft. */
    update: async (id: string, patch: Partial<DraftInput>): Promise<RequisitionDetail> =>
      unwrap(await api.patch(`/requisitions/${id}`, { body: patch, silent: true })),

    submit: async (id: string): Promise<RequisitionDetail> =>
      unwrap(await api.post(`/requisitions/${id}/submit`, { silent: true })),

    /**
     * `submittedAt` is the version on screen. The server refuses
     * `stale_version` if the request was edited and re-sent since.
     */
    approve: async (
      id: string,
      submittedAt: string | null,
      inventoryLines?: InventoryLineInput[]
    ): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/approve`, {
          body: {
            submitted_at: submittedAt,
            ...(inventoryLines && inventoryLines.length ? { inventory_lines: inventoryLines } : {}),
          },
          silent: true,
        })
      ),

    deny: async (id: string, reason: string, submittedAt: string | null): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/deny`, { body: { reason, submitted_at: submittedAt }, silent: true }),
      ),

    /** Omit the reason key when blank, so an empty string is never stored as one. */
    cancel: async (id: string, reason?: string): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/cancel`, {
          body: reason && reason.trim() ? { reason: reason.trim() } : {},
          silent: true,
        })
      ),

    /** With receipts (1 to 10) it links them; without, the receipt follows later. */
    markBought: async (id: string, receiptPids?: string[]): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/bought`, {
          body: receiptPids && receiptPids.length ? { receipt_pids: receiptPids } : { receipt_to_follow: true },
          silent: true,
        })
      ),

    close: async (id: string, reason: string): Promise<RequisitionDetail> =>
      unwrap(await api.post(`/requisitions/${id}/close`, { body: { reason }, silent: true })),

    /** Arrives every package too. `scanMissId` ties a label nobody matched to this request. */
    markArrived: async (id: string, note?: string, scanMissId?: string): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/arrived`, {
          body: {
            ...(note && note.trim() ? { note: note.trim() } : {}),
            ...(scanMissId ? { scan_miss_id: scanMissId } : {}),
          },
          silent: true,
        })
      ),

    // --- Add to inventory -------------------------------------------------

    /** Turning it off also clears the catalog item on the server. */
    setLineInventory: async (id: string, lineId: string, input: LineInventoryInput): Promise<RequisitionDetail> =>
      unwrap(await api.patch(`/requisitions/${id}/lines/${lineId}/inventory`, { body: input, silent: true })),

    costHint: async (id: string, lineId: string): Promise<CostHint> =>
      (await api.get(`/requisitions/${id}/lines/${lineId}/cost-hint`, { silent: true })) as CostHint,

    /** Ties the item just received to the line. The item's notes must name this request. */
    markLineReceived: async (id: string, lineId: string, receiptItemId: string): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/lines/${lineId}/received`, {
          body: { receipt_item_id: receiptItemId },
          silent: true,
        })
      ),

    // --- Receipts ---------------------------------------------------------

    receiptCandidates: async (id: string): Promise<ReceiptCandidate[]> => {
      const data = (await api.get(`/requisitions/${id}/receipt-candidates`, { silent: true })) as {
        receipts: ReceiptCandidate[];
      };
      return data?.receipts || [];
    },

    linkable: async (receiptPid: string): Promise<LinkableRequisition[]> => {
      const qs = new URLSearchParams({ receipt: receiptPid });
      const data = (await api.get(`/requisitions/linkable?${qs.toString()}`, { silent: true })) as {
        requisitions: LinkableRequisition[];
      };
      return data?.requisitions || [];
    },

    byReceipt: async (receiptPid: string): Promise<ReceiptRequisitionRef[]> => {
      const data = (await api.get(`/requisitions/by-receipt/${encodeURIComponent(receiptPid)}`, {
        silent: true,
      })) as { requisitions: ReceiptRequisitionRef[] };
      return data?.requisitions || [];
    },

    linkReceipt: async (id: string, receiptPid: string): Promise<RequisitionDetail> =>
      unwrap(await api.post(`/requisitions/${id}/receipts`, { body: { receipt_pid: receiptPid }, silent: true })),

    unlinkReceipt: async (id: string, receiptPid: string): Promise<RequisitionDetail> =>
      unwrap(
        await api.delete(`/requisitions/${id}/receipts/${encodeURIComponent(receiptPid)}`, { silent: true })
      ),

    // --- Packages ---------------------------------------------------------

    /** The carrier is worked out on the server from the number. */
    addShipment: async (id: string, trackingNumber: string): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/shipments`, {
          body: { tracking_number: trackingNumber.trim() },
          silent: true,
        })
      ),

    removeShipment: async (id: string, shipmentId: string): Promise<RequisitionDetail> =>
      unwrap(await api.delete(`/requisitions/${id}/shipments/${shipmentId}`, { silent: true })),

    /** `scanMissId` ties a label nobody matched to the package found by hand. */
    markShipmentArrived: async (
      id: string,
      shipmentId: string,
      note?: string,
      scanMissId?: string
    ): Promise<RequisitionDetail> =>
      unwrap(
        await api.post(`/requisitions/${id}/shipments/${shipmentId}/arrived`, {
          body: {
            ...(note && note.trim() ? { note: note.trim() } : {}),
            ...(scanMissId ? { scan_miss_id: scanMissId } : {}),
          },
          silent: true,
        })
      ),

    /** A shipping label off the receiving scan box. A miss is logged on the server. */
    scan: async (raw: string): Promise<ScanResult> => {
      const data = (await api.post('/requisitions/scan', {
        body: { raw: raw.trim().slice(0, 1000) },
        silent: true,
      })) as { match?: ScanMatch; matches?: ScanMatch[]; miss?: { id: string } };
      if (data?.match) return { kind: 'match', match: data.match };
      if (data?.matches && data.matches.length) return { kind: 'matches', matches: data.matches };
      return { kind: 'miss', missId: data?.miss?.id || '' };
    },

    getSettings: (): Promise<RequisitionSettings> => api.get('/requisitions/settings'),

    putSettings: async (settings: RequisitionSettings): Promise<RequisitionSettings> => {
      const data = (await api.put('/requisitions/settings', { body: settings })) as {
        settings: RequisitionSettings;
      };
      return data.settings;
    },

    // Borrowed from the receipts module: the same "Bought for" classes and the
    // same show search the receipt review page uses. Both routes sit behind
    // requireUserSession only, so every employee can read them.
    purchaseClasses: async (): Promise<PurchaseClassOption[]> => {
      const data = (await api.get('/receipts/form-options', { silent: true })) as {
        classes?: PurchaseClassOption[];
      };
      return data?.classes || [];
    },

    eventOptions: async (params: { date?: string; q?: string }): Promise<EventOption[]> => {
      const qs = new URLSearchParams();
      if (params.date) qs.set('date', params.date);
      if (params.q) qs.set('q', params.q);
      const data = await api.get(`/receipts/event-options?${qs.toString()}`, { silent: true });
      return Array.isArray(data) ? data : [];
    },
  };
}

export type RequisitionsApi = ReturnType<typeof useRequisitionsApi>;

function unwrap(data: unknown): RequisitionDetail {
  return (data as { requisition: RequisitionDetail }).requisition;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/** Plain words for the refusals a person can do something about. */
const ERROR_COPY: Record<string, string> = {
  lines_required: 'Add at least one item before sending it for approval.',
  purpose_required: 'Say what this is for before sending it.',
  invalid_link: 'One of the links is not a web address. Links start with http:// or https://.',
  stale_version: 'This request changed after you opened it. Look at the new version and decide again.',
  self_approval: 'You asked for this one, so someone else on the approver list has to approve it.',
  not_allowed: 'You do not have access to that list.',
  reason_required: 'Add a reason so the requester knows why.',
  bought_needs_receipt_or_follow: 'Pick a receipt, or choose Receipt to follow.',
  not_linkable: 'This request cannot take a receipt any more. Reload it to see where it stands.',
  receipt_not_yours: "That receipt is on someone else's card. Ask them, or the bookkeeper, to link it.",
  shipment_exists: 'That tracking number is already on this request.',
  needs_catalog_item:
    'Pick a catalog item for every line marked for inventory first, or take the line off inventory. A buyer does it under Into stock.',
  catalog_needs_procurement: 'Only a buyer can pick the catalog item. Turn on Add to inventory and a buyer picks it.',
  invalid_part: 'That catalog item is retired or no longer exists. Pick another one.',
  inventory_locked:
    'Nothing new goes into inventory once it is bought. A line can still come off, or a buyer can pick its catalog item.',
  line_already_received: 'That item is already in stock, so it cannot change now.',
  box_already_claimed: 'That box is already recorded against another item.',
  box_part_mismatch: 'That box holds a different catalog item from this line.',
  box_not_from_request: 'That box was not received for this request.',
};

export function errorCode(err: unknown): string | null {
  const e = err as { data?: { error?: { code?: string } } };
  return e?.data?.error?.code || null;
}

/** What went wrong, in words, with what to do next where we know it. */
export function errorMessage(err: unknown, fallback: string): string {
  const code = errorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  const e = err as { data?: { error?: { message?: string } }; message?: string };
  return e?.data?.error?.message || e?.message || fallback;
}

// ---------------------------------------------------------------------------
// Money and numbers
// ---------------------------------------------------------------------------

export function toNumber(v: Numeric | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

/** Integer cents to "$12.50". No thousands separators, matching the PO module. */
export function formatCents(cents: Numeric | null | undefined): string {
  const n = toNumber(cents);
  if (n === null) return '—';
  const sign = n < 0 ? '-' : '';
  return `${sign}$${(Math.abs(n) / 100).toFixed(2)}`;
}

/** Integer cents to a bare dollar number for Export CSV; absent stays empty. */
export function centsToDollars(cents: Numeric | null | undefined): number | null {
  const n = toNumber(cents);
  return n === null ? null : n / 100;
}

/**
 * Dollars as typed to integer cents. Tolerates a leading "$" and commas.
 * Returns undefined for blank (no estimate) and null for something unreadable.
 */
export function dollarsToCents(raw: string): number | null | undefined {
  const cleaned = raw.replace(/[$,\s]/g, '');
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function centsToInput(cents: Numeric | null | undefined): string {
  const n = toNumber(cents);
  return n === null ? '' : (n / 100).toFixed(2);
}

/** Quantity for display: whole numbers without a trailing ".00". */
export function formatQuantity(q: Numeric | null | undefined): string {
  const n = toNumber(q);
  if (n === null) return '—';
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** One word per status, from the requester's side of the screen. */
export const STATUS_LABELS: Record<RequisitionStatus, string> = {
  draft: 'Draft',
  pending_approval: 'Waiting for approval',
  approved: 'Ready to buy',
  bought: 'Bought',
  arrived: 'Arrived',
  closed: "Won't buy",
  cancelled: 'Cancelled',
};

/**
 * Tone per status. The word in the chip carries the state; this only
 * reinforces it. Tinted fills, never solid: the chips repeat on every row.
 */
export const STATUS_TONES: Record<RequisitionStatus, string> = {
  draft: 'bg-surface-container-highest text-on-surface-variant',
  pending_approval: 'bg-amber-500/15 text-amber-300',
  approved: 'bg-primary/15 text-primary-ink',
  bought: 'bg-sky-500/15 text-sky-300',
  arrived: 'bg-emerald-500/15 text-emerald-300',
  closed: 'bg-surface-container-highest text-on-surface-variant',
  cancelled: 'bg-surface-container-highest text-on-surface-variant',
};

/** Carrier names as printed on the label. */
export const CARRIER_LABELS: Record<Carrier, string> = {
  ups: 'UPS',
  usps: 'USPS',
  fedex: 'FedEx',
  amazon: 'Amazon',
  other: 'Other carrier',
};

/** Packages that have arrived, out of how many. */
export function packageCount(shipments: Shipment[]): { arrived: number; total: number } {
  return { arrived: shipments.filter((s) => s.arrived_at).length, total: shipments.length };
}

export const URGENCY_LABELS: Record<Urgency, string> = {
  low: 'Low',
  normal: 'Normal',
  urgent: 'Urgent',
};

/** A link a person can type without the scheme ("amazon.com/dp/..."). */
export function normalizeLink(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return t; // another scheme: left for the check to refuse
  if (/^[\w-]+(\.[\w-]+)+/.test(t)) return `https://${t}`;
  return t;
}

export function isWebLink(raw: string): boolean {
  return /^https?:\/\/\S+$/i.test(raw.trim());
}
